import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Box from "@mui/material/Box";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { axiosReqConfig } from "../functions/axiosRequest";
import { API_IP, STRIPE_KEY } from "../global_variables";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import PaymentModal from "./AccountComponents/PaymentModal";
import EtransferModal from "./AccountComponents/EtransferModal";
import MethodChoiceModal from "./AccountComponents/MethodChoiceModal";
import RatingModal from "./RatingModal";
import "./BookingHistory.css";

const BookingScreen = () => {
  const [activeTab, setActiveTab] = useState("In Process");
  const [bookings, setBookings] = useState([]);
  const [unratedBookings, setUnratedBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(7);
  const [paymentVisible, setPaymentVisible] = useState(false);

  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [bookingToRate, setBookingToRate] = useState(null);

  const [clientSecret, setClientSecret] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [customerSession, setCustomerSession] = useState("");
  const [selectedBooking, setSelectedBooking] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [methodChoiceOpen, setMethodChoiceOpen] = useState(false);
  const [refresh, isRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBookings, setIsFetchingBookings] = useState(true);
  const stripePromise = loadStripe(STRIPE_KEY);

  const togglePayment = () => setPaymentVisible(!paymentVisible);
  const toggleRefresh = () => isRefresh((prev) => !prev);

  const steps = ["Booking", "Wait for Response (Provider)", "Payment", "Completion"];

  // Tab counts
  const inProcessCount = bookings.filter((b) => !b.completed).length;
  const completedCount = bookings.filter((b) => !!b.completed).length;

  // Filtered list
  const filteredBookings = bookings.filter((booking) => {
    const isCompleted = !!booking.completed;
    return activeTab === "Completed" ? isCompleted : !isCompleted;
  });

  const totalItems = filteredBookings.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  // ── Stepper styling ──────────────────────────────────────────
  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: { top: 22 },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundImage:
          "linear-gradient(136deg, rgb(255,235,59) 0%, rgb(255,204,0) 50%, rgb(230,200,0) 100%)",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundImage:
          "linear-gradient(136deg, rgb(200,255,200) 0%, rgb(144,238,144) 50%, rgb(46,139,87) 100%)",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 3,
      border: 0,
      backgroundColor: "#eaeaf0",
      borderRadius: 1,
      ...theme.applyStyles("dark", { backgroundColor: theme.palette.grey[800] }),
    },
  }));

  const ColorlibStepIconRoot = styled("div")(({ theme }) => ({
    backgroundColor: "#ccc",
    zIndex: 1,
    color: "#fff",
    width: 50,
    height: 50,
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    ...theme.applyStyles("dark", { backgroundColor: theme.palette.grey[700] }),
    variants: [
      {
        props: ({ ownerState }) => ownerState.active,
        style: {
          backgroundImage: "linear-gradient(135deg, #f59e0b, #d97706)",
          boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
        },
      },
      {
        props: ({ ownerState }) => ownerState.completed,
        style: {
          backgroundImage: "linear-gradient(135deg, #059669, #047857)",
          boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
        },
      },
    ],
  }));

  function ColorlibStepIcon(props) {
    const { active, completed, className } = props;
    const icons = {
      1: <img src={require(`../images/Booking-icon.png`)} className="BookingIcon" alt="icon" />,
      2: <img src={require(`../images/wait-icon.png`)} className="BookingIcon" alt="icon" />,
      3: <img src={require(`../images/payment-icon.png`)} className="BookingIcon" alt="icon" />,
      4: <img src={require(`../images/completion-icon.png`)} className="BookingIcon" alt="icon" />,
    };
    return (
      <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
        {icons[String(props.icon)]}
      </ColorlibStepIconRoot>
    );
  }

  // ── Data fetching ────────────────────────────────────────────
  const fetchBookingInfo = useCallback(async () => {
    setIsFetchingBookings(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/dashboard`, "get", {});
      const data = res?.ok ? res.data : null;
      setBookings(Array.isArray(data?.bookings) ? data.bookings : []);
      setIsFetchingBookings(false);
      return data;
    } catch (error) {
      console.error("Error fetching booking info:", error);
      setIsFetchingBookings(false);
      return null;
    }
  }, [refresh]);

  const fetchUnratedBookings = useCallback(async () => {
    try {
      const res = await axiosReqConfig(`${API_IP}/api/rating`, "get", {});
      if (res?.ok && Array.isArray(res.data)) setUnratedBookings(res.data);
    } catch (error) {
      console.error("Error fetching unrated bookings:", error);
    }
  }, [refresh]);

  function formatDateWithDay(dateString) {
    const date = new Date(dateString);
    return `${format(date, "EEE", { locale: enGB })} ${format(date, "d MMMM yyyy", { locale: enGB })}`;
  }

  // Step 1: user clicks "Pay To Process" -> just open the method-choice modal.
  // No Stripe/etransfer call yet. The choice modal fetches the per-method totals
  // and reports back via handleMethodChoose, which then triggers the actual flow.
  function requestPayment(booking) {
    setSelectedBooking(booking);
    setMethodChoiceOpen(true);
  }

  // Step 2: user picked a method in MethodChoiceModal.
  async function handleMethodChoose(method) {
    setMethodChoiceOpen(false);
    if (method === "etransfer") {
      setPaymentMethod("etransfer");
      setPaymentVisible(true);
      return;
    }
    // Card path: create the Stripe PaymentIntent now, then open the card modal.
    setIsLoading(true);
    try {
      const res = await axiosReqConfig(`${API_IP}/api/payment`, "post", {
        booking_id: selectedBooking.booking_id,
        service_id: selectedBooking.care_service_id,
        provider_id: selectedBooking.provider_id,
      });
      if (!res?.ok) { setIsLoading(false); return; }
      const { clientSecret, totalPrice, transactionId, customerSession } = res.data;
      setSelectedBooking((prev) => ({ ...prev, transactionId }));
      setTotalPrice(totalPrice);
      setCustomerSession(customerSession);
      setClientSecret(clientSecret);
      setPaymentMethod("card");
      setPaymentVisible(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleOpenRating = (booking) => {
    setBookingToRate(booking);
    setRatingModalOpen(true);
  };

  // ── Booking Card ─────────────────────────────────────────────
  const BookingInfoDiv = ({ booking, index }) => {
    const [isFooterExpand, setIsFooterExpand] = useState(false);
    const start_date = formatDateWithDay(booking.start_date);

    // Step logic
    let currentStep = 0;
    if (booking.completed) {
      currentStep = 4;
    } else if (!booking.provider_confirmation && !booking.paid) {
      currentStep = 1;
    } else if (!booking.paid) {
      currentStep = 2;
    } else if (booking.paid) {
      currentStep = 3;
    }

    // Status key drives card accent color
    const statusKey = booking.completed
      ? "completed"
      : booking.acceptance === false
      ? "declined"
      : booking.paid
      ? "processing"
      : booking.acceptance === true
      ? "accepted"
      : "pending";

    const isUnrated = unratedBookings.some((ub) => ub.id === booking.booking_id);

    return (
      <div
        className={`BS-card BS-card--${statusKey}`}
        style={{ animationDelay: `${index * 0.07}s` }}
      >
        {/* Header */}
        <div className="BS-card-header">
          <div className="BS-header-left">
            <div className="BS-provider-icon-wrapper">
              <img
                src={require(`../images/provider-icon.png`)}
                className="BS-provider-icon"
                alt="Provider"
              />
            </div>
            <h4>
              {booking.provider_name}
              {" — "}
              {booking.acceptance === null ? (
                <span className="BS-badge BS-badge-pending">
                  Pending{" "}
                  <img alt="Pending" src={require("../images/booking_pending_icon.png")} />
                </span>
              ) : booking.acceptance === false ? (
                <span className="BS-badge BS-badge-denied">
                  Declined{" "}
                  <img alt="Declined" src={require("../images/booking_denied_icon.png")} />
                </span>
              ) : (
                <span className="BS-badge BS-badge-accepted">
                  Accepted{" "}
                  <img alt="Accepted" src={require("../images/booking_accepted_icon.webp")} />
                </span>
              )}
            </h4>
          </div>
          <div className="BS-header-right">
            <span className="BS-booking-id-chip">#{booking.booking_id}</span>
          </div>
        </div>

        {/* Body */}
        <div className="BS-card-body">
          <div className="BS-info-block">
            <span className="BS-info-label">Start Date</span>
            <h2 className="BS-info-value">{start_date}</h2>
            <span className="BS-info-label">Service</span>
            <span className="BS-info-highlight">{booking.care_service_name}</span>
          </div>

          <div className="BS-stepper-block">
            <Stack sx={{ width: "100%" }} spacing={4}>
              <Stepper
                alternativeLabel
                activeStep={currentStep}
                connector={<ColorlibConnector />}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel StepIconComponent={ColorlibStepIcon}>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
          </div>

          <div className="BS-info-block">
            <span className="BS-info-label">Member</span>
            <h3 className="BS-info-value" style={{ fontSize: "18px" }}>
              {`${booking.first_name} ${booking.last_name}`}
            </h3>
            <span className="BS-info-label">Location</span>
            <span className="BS-info-highlight">Victoria</span>
          </div>
        </div>

        {/* Footer */}
        <div className="BS-card-footer">
          <div className="BS-footer-actions">
            <div className="BS-footer-left">
              <button
                onClick={() => setIsFooterExpand(!isFooterExpand)}
                className="BS-action-btn BS-btn-outline"
              >
                {isFooterExpand ? "Less Details" : "More Details"}
              </button>
            </div>
            <div className="BS-footer-right">
              {currentStep === 1 && (
                <button className="BS-action-btn BS-btn-disabled" disabled>
                  {booking.acceptance === null ? "Wait For Provider" : "Provider Has Declined"}
                </button>
              )}
              {currentStep === 2 && (
                <button
                  onClick={() => requestPayment(booking)}
                  className="BS-action-btn BS-btn-pay"
                  disabled={isLoading}
                >
                  {isLoading && (
                    <img
                      src={require("../images/loading_icon.gif")}
                      alt="Loading"
                      style={{ width: "18px", height: "18px" }}
                    />
                  )}
                  {isLoading ? "Processing..." : "Pay To Process"}
                </button>
              )}
              {currentStep === 4 && isUnrated && (
                <button
                  onClick={() => handleOpenRating(booking)}
                  className="BS-action-btn BS-btn-primary"
                >
                  Rate Provider
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {isFooterExpand && (
          <div className="BS-card-expand">
            <div className="BS-expand-grid">
              <div className="BS-expand-section">
                <h5>
                  Provider has <span className="BS-text-green">accepted</span> these dates
                </h5>
                {booking.accepted_dates.map((date, i) => (
                  <p key={i}>
                    <img
                      src={require(`../images/date-confirmed.png`)}
                      className="BS-date-icon"
                      alt="confirmed"
                    />
                    {formatDateWithDay(date)} ({booking.accepted_start_times[i]} –{" "}
                    {booking.accepted_end_times[i]})
                  </p>
                ))}
              </div>
              <div className="BS-expand-section">
                <h5>
                  Provider has <span className="BS-text-red">declined</span> these dates
                </h5>
                {booking.declined_dates.map((date, i) => {
                  if (booking.acceptance !== null) {
                    return (
                      <p key={i}>
                        <img
                          src={require(`../images/booking_denied_icon.png`)}
                          className="BS-date-icon"
                          alt="declined"
                        />
                        {formatDateWithDay(date)}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchBookingInfo();
    fetchUnratedBookings();
  }, [fetchBookingInfo, fetchUnratedBookings]);

  // ── Pagination helpers ───────────────────────────────────────
  const PaginationButtons = ({ className = "" }) => (
    <div className={`BS-pagination ${className}`}>
      <button
        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
        disabled={currentPage === 1}
        className="BS-page-btn"
      >
        ← Prev
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          onClick={() => setCurrentPage(n)}
          className={`BS-page-btn ${currentPage === n ? "BS-page-active" : ""}`}
        >
          {n}
        </button>
      ))}
      <button
        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
        disabled={currentPage === totalPages}
        className="BS-page-btn"
      >
        Next →
      </button>
    </div>
  );

  return (
    <>
      <RatingModal
        isOpen={ratingModalOpen}
        onClose={() => setRatingModalOpen(false)}
        booking={bookingToRate}
        onSuccess={fetchUnratedBookings}
      />

      {isLoading && !paymentVisible && (
        <div className="BS-loading-overlay">
          <div className="BS-loading-modal">
            <img src={require("../images/loading_icon.gif")} alt="Loading" />
            <p>Preparing secure payment...</p>
          </div>
        </div>
      )}

      <MethodChoiceModal
        showModal={methodChoiceOpen}
        handleClose={() => setMethodChoiceOpen(false)}
        bookingId={selectedBooking?.booking_id}
        onChoose={handleMethodChoose}
      />

      {paymentVisible && paymentMethod === "card" && clientSecret && (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, customerSessionClientSecret: customerSession }}
        >
          <PaymentModal
            showModal={paymentVisible}
            handleClose={togglePayment}
            clientSecret={clientSecret}
            customerSession={customerSession}
            totalPrice={totalPrice}
            selectedBooking={selectedBooking}
            toggleRefresh={toggleRefresh}
          />
        </Elements>
      )}

      {paymentVisible && paymentMethod === "etransfer" && (
        <EtransferModal
          showModal={paymentVisible}
          handleClose={togglePayment}
          selectedBooking={selectedBooking}
          toggleRefresh={toggleRefresh}
        />
      )}

      <div className="BS-container screen-container">
        {/* Page Header */}
        <header className="screen-header">
          <div className="screen-header-text">
            <h1 className="screen-title">Booking History</h1>
            <p className="screen-subtitle">Track and manage your care service requests</p>
          </div>
        </header>

        {/* Stats Bar */}
        {!isFetchingBookings && (
          <div className="BS-stats-bar">
            <div className="BS-stat-item">
              <span className="BS-stat-value">{bookings.length}</span>
              <span className="BS-stat-label">Total Bookings</span>
            </div>
            <div className="BS-stat-item BS-stat-item--process">
              <span className="BS-stat-value">{inProcessCount}</span>
              <span className="BS-stat-label">In Process</span>
            </div>
            <div className="BS-stat-item BS-stat-item--success">
              <span className="BS-stat-value">{completedCount}</span>
              <span className="BS-stat-label">Completed</span>
            </div>
          </div>
        )}

        {/* Filters + Pagination */}
        <div className="BS-filters">
          <div className="BS-filters-left">
            <button
              className={`BS-filter-btn ${activeTab === "In Process" ? "BS-filter-active" : ""}`}
              onClick={() => { setActiveTab("In Process"); setCurrentPage(1); }}
            >
              In Process
              <span className="BS-filter-count">{inProcessCount}</span>
            </button>
            <button
              className={`BS-filter-btn ${activeTab === "Completed" ? "BS-filter-active" : ""}`}
              onClick={() => { setActiveTab("Completed"); setCurrentPage(1); }}
            >
              Completed
              <span className="BS-filter-count">{completedCount}</span>
            </button>
          </div>
          <PaginationButtons />
        </div>

        {/* Card List */}
        <div className="BS-list-container">
          {isFetchingBookings ? (
            <div className="BS-loading-list">
              <img src={require("../images/loading_icon.gif")} alt="Loading bookings" />
              <span>Loading booking history...</span>
            </div>
          ) : currentItems.length > 0 ? (
            currentItems.map((booking, index) => (
              <BookingInfoDiv
                key={booking.booking_id || booking.id}
                booking={booking}
                index={index}
              />
            ))
          ) : (
            <div className="BS-empty-state">
              <div className="BS-empty-icon">📋</div>
              <p>No bookings in this section</p>
              <span>
                Switch to{" "}
                {activeTab === "In Process" ? "Completed" : "In Process"} to see other
                requests
              </span>
            </div>
          )}
        </div>

        {/* Bottom Pagination */}
        <PaginationButtons className="BS-pagination-bottom" />
      </div>
    </>
  );
};

export default BookingScreen;
